import Phaser from "phaser";
import "./style.css";
import gameConfig from "../assets/data/game-config.json";

type PowerUpConfig = {
  id: string;
  label: string;
  scoreMultiplier: number;
  forceMultiplier: number;
};

type ImpactGradeId = "light" | "heavy" | "critical" | "chaos";

type ImpactGradeConfig = {
  label: string;
  forceMultiplier: number;
  recoilMultiplier: number;
  shakeMultiplier: number;
  particleMultiplier: number;
  dentMultiplier: number;
  shockwaveMultiplier: number;
  speedLineMultiplier: number;
  flashAlpha: number;
  captionSize: number;
  bassMultiplier: number;
};

type ImpactGrade = {
  id: ImpactGradeId;
  config: ImpactGradeConfig;
};

type AudioStatus = "idle" | "ready" | "blocked" | "unavailable" | "muted";
type AudioContextConstructor = new () => AudioContext;
type FallbackSoundId = "test" | "punch";

type PunchConfig = {
  sessionSeconds: number;
  comboWindowMs: number;
  baseHitScore: number;
  hitForce: number;
  physicsGravityY: number;
  constraintLength: number;
  constraintStiffness: number;
  constraintDamping: number;
  bagFrictionAir: number;
  bagDensity: number;
  impactImpulse: number;
  backFlyImpulse: number;
  recoilReturn: number;
  recoilDamping: number;
  maxRecoilDepth: number;
  maxPhysicsSpeed: number;
  maxAngularVelocity: number;
  dentDepth: number;
  dentSpread: number;
  dentRecovery: number;
  hitStopMs: number;
  heavyHitCombo: number;
  criticalHitCombo: number;
  impactFlashMs: number;
  heavyShakeMultiplier: number;
  criticalRecoilMultiplier: number;
  bassDropVolume: number;
  impactCrackVolume: number;
  impactBodyVolume: number;
  impactAirVolume: number;
  dentGradeMultiplier: number;
  maxStressRelief: number;
  stressPerHit: number;
  criticalCombo: number;
  bagReturn: number;
  bagDamping: number;
  particleCount: number;
  powerUpChance: number;
  powerUpSeconds: number;
  impactCaptions: string[];
  impactGrades: Record<ImpactGradeId, ImpactGradeConfig>;
  powerUps: PowerUpConfig[];
};

type HudButton = {
  bg: Phaser.GameObjects.Rectangle;
  label: Phaser.GameObjects.Text;
  action: () => void;
};

type Dent = {
  localX: number;
  localY: number;
  strength: number;
  age: number;
  side: number;
  grade: ImpactGradeId;
};

type HitInfo = {
  worldX: number;
  worldY: number;
  localX: number;
  localY: number;
  direction: number;
};

const CONFIG = gameConfig as PunchConfig;

function makeFallbackWavDataUri(sound: FallbackSoundId): string {
  const sampleRate = 22050;
  const duration = sound === "test" ? 0.16 : 0.28;
  const sampleCount = Math.floor(sampleRate * duration);
  const dataBytes = sampleCount * 2;
  const buffer = new ArrayBuffer(44 + dataBytes);
  const view = new DataView(buffer);
  const writeString = (offset: number, value: string): void => {
    for (let index = 0; index < value.length; index += 1) {
      view.setUint8(offset + index, value.charCodeAt(index));
    }
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + dataBytes, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, "data");
  view.setUint32(40, dataBytes, true);

  for (let index = 0; index < sampleCount; index += 1) {
    const progress = index / sampleCount;
    const attack = Math.min(1, progress / 0.035);
    const envelope = attack * Math.pow(1 - progress, sound === "test" ? 0.45 : 1.05);
    const thumpFrequency = sound === "test" ? 560 + progress * 300 : 138 - progress * 84;
    const snapFrequency = sound === "test" ? 1120 : 980 + progress * 1120;
    const boomFrequency = sound === "test" ? 0 : 46 + Math.sin(progress * Math.PI) * 18;
    const thump = Math.sin((Math.PI * 2 * thumpFrequency * index) / sampleRate);
    const snap = Math.sin((Math.PI * 2 * snapFrequency * index) / sampleRate) * Math.max(0, 1 - progress * 3.8);
    const boom = sound === "test" ? 0 : Math.sin((Math.PI * 2 * boomFrequency * index) / sampleRate) * Math.max(0, 1 - progress * 1.8);
    const crack = sound === "test" ? 0 : (Math.random() * 2 - 1) * Math.max(0, 1 - progress * 9);
    const sample = Phaser.Math.Clamp((thump * 0.7 + snap * 0.28 + boom * 0.42 + crack * 0.38) * envelope, -0.96, 0.96);
    view.setInt16(44 + index * 2, sample * 32767, true);
  }

  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index]);
  }

  return `data:audio/wav;base64,${btoa(binary)}`;
}

class TinySynth {
  private context?: AudioContext;
  private output?: GainNode;
  private compressor?: DynamicsCompressorNode;
  private noiseBuffer?: AudioBuffer;
  private fallbackAudio?: Record<FallbackSoundId, HTMLAudioElement>;
  private fallbackUnlocked = false;
  private enabled = true;
  private status: AudioStatus = "idle";

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.status = enabled ? "idle" : "muted";
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  getStatus(): AudioStatus {
    if (!this.enabled) {
      return "muted";
    }

    if (this.fallbackUnlocked) {
      return "ready";
    }

    return this.status;
  }

  async unlock(playReadyBlip = false): Promise<AudioStatus> {
    if (!this.enabled) {
      this.status = "muted";
      return this.status;
    }

    const context = this.getOrCreateContext();
    if (!context) {
      if (playReadyBlip && (await this.playFallback("test", 0.72))) {
        this.status = "ready";
      }
      return this.status;
    }

    try {
      if (context.state === "suspended") {
        await context.resume();
      }

      const webReady = context.state === "running";
      const fallbackReady = playReadyBlip ? await this.playFallback("test", 0.72) : this.fallbackUnlocked;
      this.status = webReady || fallbackReady ? "ready" : "blocked";
      if (playReadyBlip && webReady) {
        this.readyBlip();
      }
    } catch {
      this.status = "blocked";
    }

    return this.status;
  }

  punch(power: number, squeak: boolean, grade: ImpactGradeConfig, reducedMotion: boolean): void {
    if (!this.enabled) {
      return;
    }

    void this.playFallback("punch", Phaser.Math.Clamp(0.54 + grade.bassMultiplier * 0.1, 0.48, 0.82));
    const context = this.getOrCreateContext();
    if (!context) {
      return;
    }
    if (context.state === "suspended") {
      void this.unlock();
      return;
    }

    this.status = context.state === "running" ? "ready" : "blocked";
    if (this.status !== "ready") {
      return;
    }

    const now = context.currentTime;
    const bass = context.createOscillator();
    const bassGain = context.createGain();
    const thump = context.createOscillator();
    const thumpGain = context.createGain();
    const slap = context.createOscillator();
    const slapGain = context.createGain();
    const click = context.createOscillator();
    const clickGain = context.createGain();
    const whoosh = context.createOscillator();
    const whooshGain = context.createGain();
    const crack = context.createBufferSource();
    const crackFilter = context.createBiquadFilter();
    const crackGain = context.createGain();
    const air = context.createBufferSource();
    const airFilter = context.createBiquadFilter();
    const airGain = context.createGain();
    const bodyBounce = context.createOscillator();
    const bodyBounceGain = context.createGain();
    const boom = context.createOscillator();
    const boomGain = context.createGain();
    const snap = context.createOscillator();
    const snapGain = context.createGain();
    const output = this.getOutput(context);
    const baseFrequency = squeak ? 620 : 110;
    const endFrequency = squeak ? 980 : 62;
    const gradeVolume = reducedMotion ? Math.max(1, grade.bassMultiplier * 0.72) : grade.bassMultiplier;
    const crackVolume = CONFIG.impactCrackVolume * grade.forceMultiplier * (reducedMotion ? 0.68 : 1);
    const bodyVolume = CONFIG.impactBodyVolume * grade.bassMultiplier * (reducedMotion ? 0.76 : 1);
    const airVolume = CONFIG.impactAirVolume * grade.forceMultiplier * (reducedMotion ? 0.55 : 1);

    bass.type = "sine";
    bass.frequency.setValueAtTime(48 + Math.min(power * 0.18, 42), now);
    bass.frequency.exponentialRampToValueAtTime(26, now + 0.18);
    bassGain.gain.setValueAtTime(0.0001, now);
    bassGain.gain.exponentialRampToValueAtTime(Math.min(0.26, CONFIG.bassDropVolume * gradeVolume), now + 0.008);
    bassGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.26);

    thump.type = "sine";
    thump.frequency.setValueAtTime(70 + Math.min(power * 0.65, 140), now);
    thump.frequency.exponentialRampToValueAtTime(34, now + 0.22);
    thumpGain.gain.setValueAtTime(0.0001, now);
    thumpGain.gain.exponentialRampToValueAtTime(Math.min(0.5, (0.1 + power / 460) * gradeVolume), now + 0.01);
    thumpGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);

    slap.type = squeak ? "square" : "triangle";
    slap.frequency.setValueAtTime(baseFrequency + power * (1.5 + grade.forceMultiplier * 0.28), now + 0.01);
    slap.frequency.exponentialRampToValueAtTime(endFrequency, now + 0.14);
    slapGain.gain.setValueAtTime(0.0001, now + 0.01);
    slapGain.gain.exponentialRampToValueAtTime(Math.min(0.24, (0.04 + power / 900) * grade.forceMultiplier), now + 0.024);
    slapGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.17);

    click.type = "square";
    click.frequency.setValueAtTime(squeak ? 1120 : 820, now);
    clickGain.gain.setValueAtTime(0.0001, now);
    clickGain.gain.exponentialRampToValueAtTime(Math.min(0.1, 0.025 + power / 1600), now + 0.006);
    clickGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.045);

    crack.buffer = this.getNoiseBuffer(context);
    crackFilter.type = "bandpass";
    crackFilter.frequency.setValueAtTime(squeak ? 3200 : 1850 + Math.min(power * 2.4, 1200), now);
    crackFilter.Q.setValueAtTime(squeak ? 2.5 : 1.35, now);
    crackGain.gain.setValueAtTime(0.0001, now);
    crackGain.gain.exponentialRampToValueAtTime(Math.min(0.32, crackVolume), now + 0.003);
    crackGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.074);

    air.buffer = this.getNoiseBuffer(context);
    airFilter.type = "lowpass";
    airFilter.frequency.setValueAtTime(760 + Math.min(power * 2.2, 1600), now);
    airGain.gain.setValueAtTime(0.0001, now + 0.006);
    airGain.gain.exponentialRampToValueAtTime(Math.min(0.12, airVolume), now + 0.018);
    airGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.11);

    whoosh.type = "sawtooth";
    whoosh.frequency.setValueAtTime(190 + power * 0.5, now);
    whoosh.frequency.exponentialRampToValueAtTime(82, now + 0.12);
    whooshGain.gain.setValueAtTime(0.0001, now);
    whooshGain.gain.exponentialRampToValueAtTime(Math.min(0.13, 0.024 * grade.forceMultiplier * grade.bassMultiplier), now + 0.016);
    whooshGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);

    bodyBounce.type = "triangle";
    bodyBounce.frequency.setValueAtTime(96 + Math.min(power * 0.22, 42), now + 0.032);
    bodyBounce.frequency.exponentialRampToValueAtTime(38, now + 0.19);
    bodyBounceGain.gain.setValueAtTime(0.0001, now + 0.026);
    bodyBounceGain.gain.exponentialRampToValueAtTime(Math.min(0.3, bodyVolume), now + 0.04);
    bodyBounceGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);

    boom.type = "sine";
    boom.frequency.setValueAtTime(42 + Math.min(power * 0.05, 20), now);
    boom.frequency.exponentialRampToValueAtTime(26, now + 0.3);
    boomGain.gain.setValueAtTime(0.0001, now);
    boomGain.gain.exponentialRampToValueAtTime(Math.min(0.22, 0.065 * grade.bassMultiplier * grade.forceMultiplier), now + 0.018);
    boomGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.32);

    snap.type = "square";
    snap.frequency.setValueAtTime(squeak ? 1800 : 1320 + Math.min(power * 1.4, 720), now);
    snap.frequency.exponentialRampToValueAtTime(squeak ? 2400 : 620, now + 0.055);
    snapGain.gain.setValueAtTime(0.0001, now);
    snapGain.gain.exponentialRampToValueAtTime(Math.min(0.11, 0.034 * grade.forceMultiplier * grade.bassMultiplier), now + 0.004);
    snapGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.065);

    bass.connect(bassGain);
    thump.connect(thumpGain);
    slap.connect(slapGain);
    click.connect(clickGain);
    crack.connect(crackFilter);
    crackFilter.connect(crackGain);
    air.connect(airFilter);
    airFilter.connect(airGain);
    whoosh.connect(whooshGain);
    bodyBounce.connect(bodyBounceGain);
    boom.connect(boomGain);
    snap.connect(snapGain);
    bassGain.connect(output);
    thumpGain.connect(output);
    slapGain.connect(output);
    clickGain.connect(output);
    crackGain.connect(output);
    airGain.connect(output);
    whooshGain.connect(output);
    bodyBounceGain.connect(output);
    boomGain.connect(output);
    snapGain.connect(output);
    bass.start(now);
    thump.start(now);
    slap.start(now + 0.01);
    click.start(now);
    crack.start(now);
    air.start(now + 0.006);
    whoosh.start(now);
    bodyBounce.start(now + 0.026);
    boom.start(now);
    snap.start(now);
    bass.stop(now + 0.22);
    thump.stop(now + 0.25);
    slap.stop(now + 0.19);
    click.stop(now + 0.055);
    crack.stop(now + 0.07);
    air.stop(now + 0.12);
    whoosh.stop(now + 0.15);
    bodyBounce.stop(now + 0.24);
    boom.stop(now + 0.34);
    snap.stop(now + 0.08);
  }

  powerUp(): void {
    if (!this.enabled) {
      return;
    }

    void this.playFallback("test", 0.42);
    const context = this.getOrCreateContext();
    if (!context || context.state !== "running") {
      void this.unlock();
      return;
    }

    const now = context.currentTime;
    [360, 540, 810].forEach((frequency, index) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const start = now + index * 0.055;
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(frequency, start);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.1, start + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.13);
      oscillator.connect(gain);
      gain.connect(this.getOutput(context));
      oscillator.start(start);
      oscillator.stop(start + 0.15);
    });
  }

  private getOutput(context: AudioContext): GainNode {
    if (!this.output || !this.compressor) {
      this.output = context.createGain();
      this.compressor = context.createDynamicsCompressor();
      this.output.gain.setValueAtTime(0.9, context.currentTime);
      this.compressor.threshold.setValueAtTime(-20, context.currentTime);
      this.compressor.knee.setValueAtTime(16, context.currentTime);
      this.compressor.ratio.setValueAtTime(7, context.currentTime);
      this.compressor.attack.setValueAtTime(0.003, context.currentTime);
      this.compressor.release.setValueAtTime(0.16, context.currentTime);
      this.output.connect(this.compressor);
      this.compressor.connect(context.destination);
    }

    return this.output;
  }

  private readyBlip(): void {
    const context = this.getOrCreateContext();
    if (!context || context.state !== "running") {
      return;
    }

    const now = context.currentTime;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(560, now);
    oscillator.frequency.exponentialRampToValueAtTime(840, now + 0.055);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.075, now + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
    oscillator.connect(gain);
    gain.connect(this.getOutput(context));
    oscillator.start(now);
    oscillator.stop(now + 0.13);
  }

  private async playFallback(sound: FallbackSoundId, volume: number): Promise<boolean> {
    if (!this.enabled) {
      return false;
    }

    try {
      const template = this.getFallbackAudio(sound);
      const audio = template.cloneNode(true) as HTMLAudioElement;
      audio.volume = Phaser.Math.Clamp(volume, 0, 1);
      audio.currentTime = 0;
      await audio.play();
      this.fallbackUnlocked = true;
      this.status = "ready";
      audio.addEventListener("ended", () => audio.remove(), { once: true });
      return true;
    } catch {
      if (this.status !== "ready") {
        this.status = "blocked";
      }
      return false;
    }
  }

  private getFallbackAudio(sound: FallbackSoundId): HTMLAudioElement {
    if (!this.fallbackAudio) {
      this.fallbackAudio = {
        test: new Audio(makeFallbackWavDataUri("test")),
        punch: new Audio(makeFallbackWavDataUri("punch"))
      };
      Object.values(this.fallbackAudio).forEach((audio) => {
        audio.preload = "auto";
      });
    }

    return this.fallbackAudio[sound];
  }

  private getNoiseBuffer(context: AudioContext): AudioBuffer {
    if (!this.noiseBuffer) {
      const length = Math.floor(context.sampleRate * 0.18);
      const buffer = context.createBuffer(1, length, context.sampleRate);
      const data = buffer.getChannelData(0);
      for (let index = 0; index < length; index += 1) {
        const fade = 1 - index / length;
        data[index] = (Math.random() * 2 - 1) * fade;
      }
      this.noiseBuffer = buffer;
    }

    return this.noiseBuffer;
  }

  private getOrCreateContext(): AudioContext | undefined {
    if (!this.context) {
      const audioWindow = window as Window & { webkitAudioContext?: AudioContextConstructor };
      const ContextClass = window.AudioContext ?? audioWindow.webkitAudioContext;
      if (!ContextClass) {
        this.status = "unavailable";
        return undefined;
      }

      try {
        this.context = new ContextClass();
      } catch {
        this.status = "blocked";
        return undefined;
      }
    }

    this.status = this.context.state === "running" ? "ready" : "idle";

    return this.context;
  }
}

class PunchScene extends Phaser.Scene {
  private synth = new TinySynth();
  private bag!: Phaser.GameObjects.Container;
  private bagBody!: Phaser.GameObjects.Graphics;
  private bagPhysics!: MatterJS.BodyType;
  private bagConstraint!: MatterJS.ConstraintType;
  private rope!: Phaser.GameObjects.Graphics;
  private scoreText!: Phaser.GameObjects.Text;
  private comboText!: Phaser.GameObjects.Text;
  private timerText!: Phaser.GameObjects.Text;
  private statusText!: Phaser.GameObjects.Text;
  private powerText!: Phaser.GameObjects.Text;
  private stressBack!: Phaser.GameObjects.Rectangle;
  private stressFill!: Phaser.GameObjects.Rectangle;
  private buttons: HudButton[] = [];
  private score = 0;
  private combo = 0;
  private stress = 0;
  private remainingMs = CONFIG.sessionSeconds * 1000;
  private lastHitAt = -CONFIG.comboWindowMs;
  private activePowerUp?: PowerUpConfig;
  private activePowerUntil = 0;
  private paused = false;
  private ended = false;
  private reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  private squash = 0;
  private damage = 0;
  private impactScale = 0;
  private recoilDepth = 0;
  private recoilVelocity = 0;
  private dents: Dent[] = [];
  private hitStopUntil = 0;
  private bagRestX = 0;
  private bagRestY = 0;
  private ropeTopY = 78;
  private keyboardSide = -1;
  private statusMessage = "";
  private statusMessageUntil = 0;
  private firstSoundRequested = false;

  constructor() {
    super("PunchScene");
  }

  create(): void {
    this.cameras.main.setBackgroundColor("#f4ead7");
    this.matter.world.setGravity(0, CONFIG.physicsGravityY);
    this.createArena();
    this.createBag();
    this.createHud();
    this.registerInput();
    this.layout();
    this.showStatusMessage("CLICK BAG FOR SOUND", 1800);
    this.updateHud();
  }

  update(_time: number, delta: number): void {
    if (!this.paused && !this.ended) {
      this.remainingMs = Math.max(0, this.remainingMs - delta);
      if (this.remainingMs === 0) {
        this.ended = true;
        this.statusText.setText("TIME! PRESS R OR RESTART");
      }
    }

    if (this.activePowerUp && this.time.now > this.activePowerUntil) {
      this.activePowerUp = undefined;
    }

    this.updateDents(delta);
    this.updateBag(delta);
    this.publishDebugState();
    this.updateHud();
  }

  private createArena(): void {
    const { width, height } = this.scale;
    const floor = this.add.rectangle(width / 2, height - 48, width, 96, 0x383247, 1);
    floor.setDepth(-3);

    const mat = this.add.ellipse(width / 2, height - 70, 410, 48, 0xf66f4d, 0.3);
    mat.setDepth(-2);

    const sign = this.add
      .text(width / 2, 28, "PUNCH PANIC!", {
        fontFamily: "Arial Black, Impact, sans-serif",
        fontSize: "32px",
        color: "#2f2340",
        align: "center"
      })
      .setOrigin(0.5, 0);
    sign.setName("title");
  }

  private createBag(): void {
    this.rope = this.add.graphics();
    this.bagRestX = this.scale.width / 2;
    this.bagRestY = Math.max(250, this.scale.height * 0.52);
    this.ropeTopY = this.scale.width < 620 ? 86 : 78;
    this.bagPhysics = this.matter.add.rectangle(this.bagRestX, this.bagRestY, 118, 222, {
      chamfer: { radius: 50 },
      frictionAir: CONFIG.bagFrictionAir,
      restitution: 0.24,
      density: CONFIG.bagDensity,
      label: "punching-bag"
    });
    this.bagConstraint = this.matter.add.worldConstraint(
      this.bagPhysics,
      CONFIG.constraintLength,
      CONFIG.constraintStiffness,
      {
        damping: CONFIG.constraintDamping,
        pointA: { x: this.bagRestX, y: this.ropeTopY },
        pointB: { x: 0, y: -116 }
      }
    );
    this.bag = this.add.container(0, 0);
    this.bagBody = this.add.graphics();
    this.bag.add(this.bagBody);
    this.bag.setDepth(2);
    this.redrawBag();
  }

  private createHud(): void {
    const textStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: "Arial, sans-serif",
      fontSize: "20px",
      color: "#211829"
    };

    this.scoreText = this.add.text(22, 18, "", textStyle);
    this.comboText = this.add.text(22, 46, "", textStyle);
    this.timerText = this.add.text(22, 74, "", textStyle);
    this.powerText = this.add
      .text(0, 0, "", {
        fontFamily: "Arial Black, Impact, sans-serif",
        fontSize: "22px",
        color: "#ffffff",
        stroke: "#2f2340",
        strokeThickness: 5,
        align: "center"
      })
      .setOrigin(0.5);
    this.statusText = this.add
      .text(0, 0, "HIT THE BAG", {
        fontFamily: "Arial Black, Impact, sans-serif",
        fontSize: "22px",
        color: "#ffffff",
        stroke: "#2f2340",
        strokeThickness: 5,
        align: "center"
      })
      .setOrigin(0.5);
    this.stressBack = this.add.rectangle(0, 0, 260, 16, 0x2f2340, 0.22).setOrigin(0, 0.5);
    this.stressFill = this.add.rectangle(0, 0, 0, 16, 0x34c38f, 1).setOrigin(0, 0.5);

    this.buttons = [
      this.createButton("SOUND", () => this.toggleSound()),
      this.createButton("MOTION", () => this.toggleMotion()),
      this.createButton("PAUSE", () => this.togglePause()),
      this.createButton("RESTART", () => this.restartRound())
    ];
  }

  private createButton(label: string, action: () => void): HudButton {
    const bg = this.add.rectangle(0, 0, 96, 36, 0x2f2340, 0.92).setInteractive({
      useHandCursor: true
    });
    const text = this.add
      .text(0, 0, label, {
        fontFamily: "Arial Black, Impact, sans-serif",
        fontSize: "13px",
        color: "#ffffff",
        align: "center"
      })
      .setOrigin(0.5);

    bg.on("pointerdown", action);
    text.setInteractive({ useHandCursor: true }).on("pointerdown", action);
    return { bg, label: text, action };
  }

  private registerInput(): void {
    this.input.on("pointerdown", async (pointer: Phaser.Input.Pointer) => {
      if (pointer.y < (this.scale.width < 620 ? 86 : 58)) {
        return;
      }
      const hit = this.getHitInfo(pointer.x, pointer.y);
      if (hit) {
        await this.unlockSound(!this.firstSoundRequested);
        this.firstSoundRequested = true;
        this.punch(hit);
      }
    });

    this.input.keyboard?.on("keydown-SPACE", async () => {
      this.keyboardSide *= -1;
      await this.unlockSound(!this.firstSoundRequested);
      this.firstSoundRequested = true;
      this.punch(this.getKeyboardHitInfo(this.keyboardSide));
    });
    this.input.keyboard?.on("keydown-P", () => this.togglePause());
    this.input.keyboard?.on("keydown-R", () => this.restartRound());
    this.input.keyboard?.on("keydown-M", () => this.toggleSound());
    this.input.keyboard?.on("keydown-C", () => this.toggleMotion());
    this.scale.on("resize", () => this.layout());
  }

  private punch(hit: HitInfo): void {
    if (this.paused || this.ended) {
      return;
    }

    const now = this.time.now;
    this.combo = now - this.lastHitAt <= CONFIG.comboWindowMs ? this.combo + 1 : 1;
    this.lastHitAt = now;

    if (!this.activePowerUp && Math.random() < CONFIG.powerUpChance) {
      this.activePowerUp = Phaser.Utils.Array.GetRandom(CONFIG.powerUps);
      this.activePowerUntil = now + CONFIG.powerUpSeconds * 1000;
      this.synth.powerUp();
      this.flashCaption(this.activePowerUp.label, "#34c38f", 46);
    }

    const scoreMultiplier = this.activePowerUp?.scoreMultiplier ?? 1;
    const forceMultiplier = this.activePowerUp?.forceMultiplier ?? 1;
    const scoreGain = Math.round((CONFIG.baseHitScore + this.combo) * scoreMultiplier);
    const comboBoost = 1 + Math.min(this.combo, CONFIG.criticalCombo) / 18;
    const verticalLeverage = Phaser.Math.Clamp((hit.localY + 112) / 224, 0.35, 1.15);
    const edgeLeverage = Phaser.Math.Clamp(Math.abs(hit.localX) / 58, 0.55, 1.25);
    const baseForce = CONFIG.hitForce * forceMultiplier * comboBoost * verticalLeverage * edgeLeverage;
    const grade = this.getImpactGrade(hit, baseForce);
    const gradeConfig = grade.config;
    const force = baseForce * gradeConfig.forceMultiplier;
    const impulse = CONFIG.impactImpulse * forceMultiplier * comboBoost * gradeConfig.forceMultiplier;
    const verticalKick = Phaser.Math.Clamp(hit.localY / 130, -0.42, 0.36);
    const forceVector = {
      x: hit.direction * impulse,
      y: verticalKick * impulse * 0.4 - impulse * CONFIG.backFlyImpulse * 0.18 * gradeConfig.recoilMultiplier
    };

    this.score += scoreGain;
    this.stress = Math.min(CONFIG.maxStressRelief, this.stress + CONFIG.stressPerHit * scoreMultiplier);
    this.damage = Math.min(1, this.damage + 0.035 * gradeConfig.dentMultiplier);
    this.squash = 1;
    this.impactScale = Math.max(this.impactScale, Phaser.Math.Clamp(force / 500, 0.2, 0.58));
    const criticalRecoil = grade.id === "critical" || grade.id === "chaos" ? CONFIG.criticalRecoilMultiplier : 1;
    this.recoilVelocity += Phaser.Math.Clamp(force * CONFIG.backFlyImpulse * gradeConfig.recoilMultiplier * criticalRecoil, 48, 245);
    this.matter.body.applyForce(this.bagPhysics, { x: hit.worldX, y: hit.worldY }, forceVector);
    this.clampBodyMotion();
    this.addDent(hit, force, grade);
    this.hitStop(force, grade);

    this.emitImpact(hit, force, grade);
    this.synth.punch(force, this.activePowerUp?.id === "squeak", gradeConfig, this.reducedMotion);
  }

  private getImpactGrade(hit: HitInfo, force: number): ImpactGrade {
    const lowerHit = Phaser.Math.Clamp((hit.localY + 24) / 140, 0, 1);
    const edgeHit = Phaser.Math.Clamp(Math.abs(hit.localX) / 58, 0, 1);
    const forceRatio = force / CONFIG.hitForce;
    let id: ImpactGradeId = "light";

    if (this.combo >= CONFIG.criticalHitCombo + 4 || forceRatio >= 2.15) {
      id = "chaos";
    } else if (this.combo >= CONFIG.criticalHitCombo || forceRatio >= 1.68) {
      id = "critical";
    } else if (this.combo >= CONFIG.heavyHitCombo || lowerHit > 0.76 || edgeHit > 0.82 || forceRatio >= 1.22) {
      id = "heavy";
    }

    return { id, config: CONFIG.impactGrades[id] };
  }

  private getHitInfo(worldX: number, worldY: number): HitInfo | undefined {
    const position = this.bagPhysics.position;
    const angle = this.bagPhysics.angle;
    const dx = worldX - position.x;
    const dy = worldY - position.y;
    const cos = Math.cos(-angle);
    const sin = Math.sin(-angle);
    const localX = dx * cos - dy * sin;
    const localY = dx * sin + dy * cos;
    const normalizedX = localX / 64;
    const normalizedY = localY / 118;
    const inside = normalizedX * normalizedX + normalizedY * normalizedY <= 1.08;

    if (!inside) {
      return undefined;
    }

    return {
      worldX,
      worldY,
      localX: Phaser.Math.Clamp(localX, -58, 58),
      localY: Phaser.Math.Clamp(localY, -106, 116),
      direction: localX < 0 ? 1 : -1
    };
  }

  private getKeyboardHitInfo(side: number): HitInfo {
    const localX = side < 0 ? -44 : 44;
    const localY = Phaser.Math.Between(-18, 52);
    const angle = this.bagPhysics.angle;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const worldX = this.bagPhysics.position.x + localX * cos - localY * sin;
    const worldY = this.bagPhysics.position.y + localX * sin + localY * cos;

    return {
      worldX,
      worldY,
      localX,
      localY,
      direction: side < 0 ? 1 : -1
    };
  }

  private addDent(hit: HitInfo, force: number, grade: ImpactGrade): void {
    this.dents.unshift({
      localX: hit.localX,
      localY: hit.localY,
      strength: Phaser.Math.Clamp((force / 190) * grade.config.dentMultiplier * CONFIG.dentGradeMultiplier, 0.55, 3.15),
      age: 0,
      side: hit.direction,
      grade: grade.id
    });
    this.dents.splice(8);
  }

  private hitStop(force: number, grade: ImpactGrade): void {
    const motionScale = this.reducedMotion ? 0.34 : 1;
    const duration = Phaser.Math.Clamp(
      CONFIG.hitStopMs * (force / CONFIG.hitForce) * grade.config.forceMultiplier * motionScale,
      this.reducedMotion ? 18 : 42,
      this.reducedMotion ? 40 : 118
    );
    this.hitStopUntil = Math.max(this.hitStopUntil, this.time.now + duration);
    this.matter.world.pause();
    this.time.delayedCall(duration, () => {
      if (!this.paused && !this.ended && this.time.now >= this.hitStopUntil) {
        this.matter.world.resume();
      }
    });
  }

  private clampBodyMotion(): void {
    const velocity = this.bagPhysics.velocity;
    const speed = Math.hypot(velocity.x, velocity.y);

    if (speed > CONFIG.maxPhysicsSpeed) {
      const scale = CONFIG.maxPhysicsSpeed / speed;
      this.matter.body.setVelocity(this.bagPhysics, {
        x: velocity.x * scale,
        y: velocity.y * scale
      });
    }

    const angularVelocity = Phaser.Math.Clamp(
      this.bagPhysics.angularVelocity,
      -CONFIG.maxAngularVelocity,
      CONFIG.maxAngularVelocity
    );
    if (angularVelocity !== this.bagPhysics.angularVelocity) {
      this.matter.body.setAngularVelocity(this.bagPhysics, angularVelocity);
    }
  }

  private emitImpact(hit: HitInfo, force: number, grade: ImpactGrade): void {
    const accent = this.activePowerUp?.id === "confetti" ? "#34c38f" : grade.id === "chaos" ? "#ffd166" : "#f66f4d";
    const caption = grade.id === "light" ? Phaser.Utils.Array.GetRandom(CONFIG.impactCaptions) : grade.config.label;
    this.flashCaption(caption, accent, grade.config.captionSize);
    this.emitImpactFlash(grade);
    this.pulseReliefMeter(grade);

    if (!this.reducedMotion) {
      const heavyShake = grade.id === "light" ? 1 : CONFIG.heavyShakeMultiplier;
      const shakeDuration = Phaser.Math.Clamp(force * 0.42 * grade.config.shakeMultiplier * heavyShake, 60, 165);
      const shakeIntensity = Phaser.Math.Clamp((force / 28000) * grade.config.shakeMultiplier, 0.004, 0.018);
      this.cameras.main.shake(shakeDuration, shakeIntensity);
    }

    const countBase = this.activePowerUp?.id === "confetti" ? CONFIG.particleCount * 2 : CONFIG.particleCount;
    const count = this.reducedMotion ? Math.ceil(countBase * 0.45) : Math.ceil(countBase * grade.config.particleMultiplier);
    for (let index = 0; index < count; index += 1) {
      const x = hit.worldX + Phaser.Math.Between(-8, 8);
      const y = hit.worldY + Phaser.Math.Between(-8, 8);
      const colors =
        grade.id === "light"
          ? [0xf66f4d, 0x34c38f, 0x4f8cff, 0xffd166, 0xffffff]
          : [0xffd166, 0xffffff, 0xf66f4d, 0x4f8cff];
      const color = Phaser.Utils.Array.GetRandom(colors);
      const particle = this.add.circle(x, y, Phaser.Math.Between(3, grade.id === "light" ? 8 : 11), color, 1);
      const travel = this.reducedMotion ? 30 : Phaser.Math.Between(90, 185) * grade.config.speedLineMultiplier;
      this.tweens.add({
        targets: particle,
        x: x + hit.direction * travel + Phaser.Math.Between(-35, 35),
        y: y + Phaser.Math.Between(-80, 60),
        alpha: 0,
        scale: 0.2,
        duration: this.reducedMotion ? 220 : Phaser.Math.Between(360, 620),
        ease: "Cubic.easeOut",
        onComplete: () => particle.destroy()
      });
    }

    this.emitShockwave(hit, force, grade);
    this.emitSpeedLines(hit, force, grade);
    this.tweens.add({
      targets: this.bag,
      scaleX: 1 + Math.min((force / 720) * grade.config.forceMultiplier, 0.42),
      scaleY: 1 - Math.min((force / 980) * grade.config.forceMultiplier, 0.26),
      yoyo: true,
      duration: this.reducedMotion ? 50 : 90,
      ease: "Quad.easeOut"
    });
  }

  private emitImpactFlash(grade: ImpactGrade): void {
    if (grade.config.flashAlpha <= 0) {
      return;
    }

    const flash = this.add.rectangle(0, 0, this.scale.width, this.scale.height, 0xffffff, this.reducedMotion ? grade.config.flashAlpha * 0.28 : grade.config.flashAlpha);
    flash.setOrigin(0, 0);
    flash.setDepth(9);
    this.tweens.add({
      targets: flash,
      alpha: 0,
      duration: this.reducedMotion ? CONFIG.impactFlashMs * 0.45 : CONFIG.impactFlashMs,
      ease: "Quad.easeOut",
      onComplete: () => flash.destroy()
    });
  }

  private pulseReliefMeter(grade: ImpactGrade): void {
    if (grade.id === "light" && this.stress < CONFIG.maxStressRelief) {
      return;
    }

    this.tweens.add({
      targets: [this.stressBack, this.stressFill],
      scaleY: this.reducedMotion ? 1.08 : 1.28,
      yoyo: true,
      duration: this.reducedMotion ? 80 : 130,
      ease: "Quad.easeOut"
    });
  }

  private flashCaption(text: string, color: string, size: number): void {
    const caption = this.add
      .text(this.bag.x, this.bag.y - 125, text, {
        fontFamily: "Arial Black, Impact, sans-serif",
        fontSize: `${size}px`,
        color,
        stroke: "#2f2340",
        strokeThickness: 7,
        align: "center"
      })
      .setOrigin(0.5);

    caption.setAngle(Phaser.Math.Between(-8, 8));
    this.tweens.add({
      targets: caption,
      y: caption.y - (this.reducedMotion ? 24 : 76),
      alpha: 0,
      scale: this.reducedMotion ? 1 : 1.18,
      duration: this.reducedMotion ? 360 : 760,
      ease: "Cubic.easeOut",
      onComplete: () => caption.destroy()
    });
  }

  private emitShockwave(hit: HitInfo, force: number, grade: ImpactGrade): void {
    if (this.reducedMotion) {
      return;
    }

    const ring = this.add.circle(hit.worldX, hit.worldY, 12, 0xffffff, 0);
    ring.setStrokeStyle(grade.id === "light" ? 5 : 7, 0xffffff, grade.id === "light" ? 0.9 : 1);
    this.tweens.add({
      targets: ring,
      radius: Phaser.Math.Clamp(force * 0.34 * grade.config.shockwaveMultiplier, 54, 155),
      alpha: 0,
      duration: grade.id === "light" ? 260 : 320,
      ease: "Quad.easeOut",
      onComplete: () => ring.destroy()
    });

    if (grade.id === "critical" || grade.id === "chaos") {
      const core = this.add.circle(hit.worldX, hit.worldY, 8, 0xffd166, 0.52);
      core.setDepth(8);
      this.tweens.add({
        targets: core,
        radius: Phaser.Math.Clamp(force * 0.16, 28, 58),
        alpha: 0,
        duration: 150,
        ease: "Quad.easeOut",
        onComplete: () => core.destroy()
      });
    }
  }

  private emitSpeedLines(hit: HitInfo, force: number, grade: ImpactGrade): void {
    if (this.reducedMotion) {
      return;
    }

    const lineCount = Phaser.Math.Clamp(Math.round((force / 34) * grade.config.speedLineMultiplier), 4, 18);
    for (let index = 0; index < lineCount; index += 1) {
      const y = hit.worldY + Phaser.Math.Between(-42, 42);
      const startX = hit.worldX - hit.direction * Phaser.Math.Between(22, 56);
      const endX = startX - hit.direction * Phaser.Math.Between(42, 98) * grade.config.speedLineMultiplier;
      const line = this.add.graphics();
      line.lineStyle(grade.id === "light" ? 4 : 6, grade.id === "chaos" ? 0xf66f4d : 0x2f2340, grade.id === "light" ? 0.55 : 0.68);
      line.beginPath();
      line.moveTo(startX, y);
      line.lineTo(endX, y + Phaser.Math.Between(-10, 10));
      line.strokePath();
      this.tweens.add({
        targets: line,
        alpha: 0,
        x: line.x - hit.direction * 32 * grade.config.speedLineMultiplier,
        duration: grade.id === "light" ? 180 : 230,
        ease: "Quad.easeOut",
        onComplete: () => line.destroy()
      });
    }
  }

  private updateDents(delta: number): void {
    const seconds = delta / 1000;
    this.dents.forEach((dent) => {
      dent.age += seconds * CONFIG.dentRecovery;
      dent.strength = Math.max(0, dent.strength - seconds * CONFIG.dentRecovery * 0.35);
    });
    this.dents = this.dents.filter((dent) => dent.strength > 0.05 && dent.age < 1.9);
  }

  private updateBag(delta: number): void {
    const seconds = delta / 1000;
    this.squash = Math.max(0, this.squash - seconds * 6);
    this.impactScale = Math.max(0, this.impactScale - seconds * 5.5);
    this.recoilVelocity += -this.recoilDepth * CONFIG.recoilReturn * seconds;
    this.recoilVelocity *= Math.pow(CONFIG.recoilDamping, delta / 16.67);
    this.recoilDepth = Phaser.Math.Clamp(this.recoilDepth + this.recoilVelocity * seconds, 0, CONFIG.maxRecoilDepth);
    this.damage = Math.max(0, this.damage - seconds * 0.025);
    this.clampBodyMotion();

    this.bag.x = this.bagPhysics.position.x;
    this.bag.y = this.bagPhysics.position.y - this.recoilDepth * 0.42;
    this.bag.rotation = this.bagPhysics.angle;
    const recoilScale = 1 - this.recoilDepth * 0.0025;
    this.bag.scaleX = recoilScale * (1 + this.squash * 0.12 + this.impactScale * 0.1);
    this.bag.scaleY = recoilScale * (1 - this.squash * 0.08 - this.impactScale * 0.08);
    this.redrawBag();

    this.rope.clear();
    this.rope.lineStyle(8, 0x2f2340, 1);
    this.rope.beginPath();
    this.rope.moveTo(this.bagRestX, this.ropeTopY);
    const topX = this.bag.x + Math.sin(this.bag.rotation) * 116 * recoilScale;
    const topY = this.bag.y - Math.cos(this.bag.rotation) * 116 * recoilScale;
    this.rope.lineTo(topX, topY);
    this.rope.strokePath();
  }

  private redrawBag(): void {
    const heat = Phaser.Math.Linear(0xf7a072, 0xf66f4d, this.damage);
    this.bagBody.clear();
    this.bagBody.fillStyle(0x2f2340, 0.2);
    this.bagBody.fillEllipse(10, 128, 124, 28);
    this.bagBody.fillStyle(heat, 1);
    this.bagBody.lineStyle(8, 0x2f2340, 1);
    this.bagBody.fillRoundedRect(-62, -110, 124, 230, 50);
    this.bagBody.strokeRoundedRect(-62, -110, 124, 230, 50);
    this.bagBody.fillStyle(0xffd166, 1);
    this.bagBody.fillRoundedRect(-48, -32, 96, 58, 12);
    this.bagBody.lineStyle(4, 0x2f2340, 1);
    this.bagBody.strokeRoundedRect(-48, -32, 96, 58, 12);
    this.bagBody.fillStyle(0x2f2340, 1);
    this.bagBody.fillCircle(-26, -62, 7 + this.squash * 4);
    this.bagBody.fillCircle(26, -62, 7 + this.squash * 4);
    this.bagBody.lineStyle(5, 0x2f2340, 1);
    this.bagBody.beginPath();
    this.bagBody.arc(0, -42, 22, 0.15, Math.PI - 0.15);
    this.bagBody.strokePath();

    this.dents.forEach((dent) => {
      const dentGrade = CONFIG.impactGrades[dent.grade];
      const depth = CONFIG.dentDepth * dent.strength * dentGrade.dentMultiplier * (1 - Phaser.Math.Clamp(dent.age / 1.9, 0, 1));
      const spread = CONFIG.dentSpread;
      const edgeFactor = Phaser.Math.Clamp((Math.abs(dent.localX) - 26) / 26, 0, 1);
      const impactSide = dent.localX < 0 ? -1 : 1;
      const edgeX = impactSide * 62;
      const dentX = Phaser.Math.Clamp(dent.localX + dent.side * depth * 0.12, -44, 44);
      const dentY = dent.localY;

      this.bagBody.fillStyle(0x2f2340, Phaser.Math.Clamp(0.34 * dentGrade.dentMultiplier, 0.34, 0.58));
      this.bagBody.fillEllipse(dentX, dentY, depth * 2.65, depth * 1.28 * spread);
      this.bagBody.fillStyle(0x000000, Phaser.Math.Clamp(0.18 * dentGrade.dentMultiplier, 0.18, 0.34));
      this.bagBody.fillEllipse(dentX + dent.side * depth * 0.14, dentY + 2, depth * 1.7, depth * 0.8 * spread);
      this.bagBody.lineStyle(dent.grade === "light" ? 5 : 7, 0xffffff, Phaser.Math.Clamp(0.58 * dentGrade.dentMultiplier, 0.58, 0.82));
      this.bagBody.strokeEllipse(dentX - dent.side * depth * 0.24, dentY - 2, depth * 1.95, depth * 0.84 * spread);

      if (edgeFactor > 0) {
        this.bagBody.fillStyle(0xf4ead7, 0.92 * edgeFactor);
        this.bagBody.fillEllipse(
          edgeX + impactSide * 3,
          dentY,
          depth * 1.2 * edgeFactor,
          depth * 1.85 * spread * edgeFactor
        );
        this.bagBody.lineStyle(5, 0x2f2340, 0.74 * edgeFactor);
        this.bagBody.strokeEllipse(
          edgeX + impactSide * 2,
          dentY,
          depth * 0.86 * edgeFactor,
          depth * 1.42 * spread * edgeFactor
        );
        this.bagBody.fillStyle(0xffd166, 0.44 * edgeFactor);
        this.bagBody.fillEllipse(-impactSide * 55, dentY * 0.82, depth * 1.18 * edgeFactor, depth * 0.75);
      }
    });
  }

  private updateHud(): void {
    const seconds = Math.ceil(this.remainingMs / 1000);
    const comboLabel = this.combo >= CONFIG.criticalCombo ? `${this.combo} CHAOS COMBO` : `${this.combo} combo`;
    this.scoreText.setText(`Score ${this.score}`);
    this.comboText.setText(`Combo ${comboLabel}`);
    this.timerText.setText(`Time ${seconds}s`);
    this.powerText.setText(this.activePowerUp ? this.activePowerUp.label : "");
    this.stressFill.width = (this.stress / CONFIG.maxStressRelief) * this.stressBack.width;

    const audioStatus = this.synth.getStatus();
    const soundLabel =
      audioStatus === "muted" ? "SOUND OFF" : audioStatus === "ready" ? "SOUND READY" : "SOUND ON";
    const motionLabel = this.reducedMotion ? "CALM ON" : "CALM OFF";
    const pauseLabel = this.paused ? "RESUME" : "PAUSE";
    [soundLabel, motionLabel, pauseLabel, "RESTART"].forEach((label, index) => {
      this.buttons[index].label.setText(label);
    });

    if (this.statusMessage && this.time.now < this.statusMessageUntil && !this.ended && !this.paused) {
      this.statusText.setText(this.statusMessage);
    } else if (!this.ended && !this.paused) {
      this.statusMessage = "";
      this.statusText.setText(this.stress >= CONFIG.maxStressRelief ? "RELIEF MAX!" : "HIT THE BAG");
    } else if (this.paused) {
      this.statusText.setText("PAUSED");
    }
  }

  private layout(): void {
    const { width, height } = this.scale;
    this.bagRestX = width / 2;
    this.bagRestY = Math.max(250, height * 0.52);
    this.ropeTopY = width < 620 ? 86 : 78;
    if (this.bagConstraint) {
      this.bagConstraint.pointA = { x: this.bagRestX, y: this.ropeTopY };
    }

    const title = this.children.getByName("title") as Phaser.GameObjects.Text | null;
    title?.setPosition(width / 2, width < 620 ? 52 : 24);
    title?.setFontSize(width < 620 ? 24 : 32);
    this.statusText.setPosition(width / 2, height - 34);
    this.powerText.setPosition(width / 2, 108);
    this.stressBack.setPosition(22, 112);
    this.stressFill.setPosition(22, 112);

    const buttonWidth = width < 680 ? 82 : 106;
    const gap = width < 680 ? 6 : 10;
    this.buttons.forEach((button, index) => {
      button.bg.width = buttonWidth;
      button.bg.height = 34;
      button.bg.setPosition(width - 18 - buttonWidth / 2 - index * (buttonWidth + gap), 30);
      button.label.setPosition(button.bg.x, button.bg.y);
      button.label.setFontSize(width < 680 ? 11 : 13);
    });

    if (width < 620) {
      this.scoreText.setFontSize(16);
      this.comboText.setFontSize(16);
      this.timerText.setFontSize(16);
      this.powerText.setFontSize(18);
      this.powerText.setPosition(width / 2, 204);
      this.scoreText.setPosition(16, 92);
      this.comboText.setPosition(16, 114);
      this.timerText.setPosition(16, 136);
      this.stressBack.setPosition(16, 166);
      this.stressFill.setPosition(16, 166);
      this.stressBack.width = Math.min(220, width - 32);
    } else {
      this.scoreText.setFontSize(20);
      this.comboText.setFontSize(20);
      this.timerText.setFontSize(20);
      this.powerText.setFontSize(22);
      this.powerText.setPosition(width / 2, 108);
      this.scoreText.setPosition(22, 18);
      this.comboText.setPosition(22, 46);
      this.timerText.setPosition(22, 74);
      this.stressBack.width = 260;
    }
  }

  private async toggleSound(): Promise<void> {
    if (this.synth.isEnabled() && this.synth.getStatus() === "ready") {
      await this.unlockSound(true, "TEST SOUND");
      return;
    }

    const nextEnabled = !this.synth.isEnabled();
    this.synth.setEnabled(nextEnabled);
    if (nextEnabled) {
      await this.unlockSound(true);
    } else {
      this.showStatusMessage("SOUND OFF", 900);
    }
    this.updateHud();
  }

  private async unlockSound(playReadyBlip: boolean, readyMessage = "SOUND READY"): Promise<AudioStatus> {
    const status = await this.synth.unlock(playReadyBlip);
    if (status === "ready") {
      this.showStatusMessage(readyMessage, playReadyBlip ? 1200 : 650);
    } else if (status === "blocked") {
      this.showStatusMessage("BROWSER SOUND BLOCKED", 1600);
    } else if (status === "unavailable") {
      this.showStatusMessage("AUDIO UNAVAILABLE", 1600);
    }

    this.updateHud();
    return status;
  }

  private showStatusMessage(message: string, durationMs: number): void {
    this.statusMessage = message;
    this.statusMessageUntil = this.time.now + durationMs;
  }

  private toggleMotion(): void {
    this.reducedMotion = !this.reducedMotion;
    this.updateHud();
  }

  private togglePause(): void {
    if (this.ended) {
      return;
    }
    this.paused = !this.paused;
    if (this.paused) {
      this.matter.world.pause();
    } else {
      this.matter.world.resume();
    }
    this.updateHud();
  }

  private restartRound(): void {
    this.score = 0;
    this.combo = 0;
    this.stress = 0;
    this.remainingMs = CONFIG.sessionSeconds * 1000;
    this.lastHitAt = -CONFIG.comboWindowMs;
    this.activePowerUp = undefined;
    this.activePowerUntil = 0;
    this.paused = false;
    this.ended = false;
    this.squash = 0;
    this.damage = 0;
    this.impactScale = 0;
    this.recoilDepth = 0;
    this.recoilVelocity = 0;
    this.dents = [];
    this.hitStopUntil = 0;
    this.matter.world.resume();
    this.matter.body.setPosition(this.bagPhysics, { x: this.bagRestX, y: this.bagRestY }, false);
    this.matter.body.setVelocity(this.bagPhysics, { x: 0, y: 0 });
    this.matter.body.setAngularVelocity(this.bagPhysics, 0);
    this.matter.body.setAngle(this.bagPhysics, 0, false);
    this.updateHud();
  }

  private publishDebugState(): void {
    const debugWindow = window as typeof window & {
      __punchPanicDebug?: {
        score: number;
        combo: number;
        bagX: number;
        bagY: number;
        bagAngle: number;
        dentCount: number;
      };
    };
    debugWindow.__punchPanicDebug = {
      score: this.score,
      combo: this.combo,
      bagX: this.bagPhysics.position.x,
      bagY: this.bagPhysics.position.y,
      bagAngle: this.bagPhysics.angle,
      dentCount: this.dents.length
    };
  }
}

const game = new Phaser.Game({
  type: Phaser.AUTO,
  parent: "app",
  backgroundColor: "#f4ead7",
  scale: {
    mode: Phaser.Scale.RESIZE,
    autoCenter: Phaser.Scale.CENTER_BOTH,
    width: window.innerWidth,
    height: window.innerHeight,
    min: {
      width: 320,
      height: 480
    }
  },
  render: {
    antialias: true,
    pixelArt: false
  },
  physics: {
    default: "matter",
    matter: {
      debug: false,
      gravity: {
        x: 0,
        y: CONFIG.physicsGravityY
      }
    }
  },
  scene: PunchScene
});

window.addEventListener("beforeunload", () => {
  game.destroy(true);
});
